<template>
  <section class="should-you-bluff-final-conclusion well" id="should-you-bluff-final-conclusion">
    <header class="page-header">
      <h1>Subsequent analysis that factors in future streets</h1>
    </header>
    <table v-if="ableToRunEquityCalculations" class="table">
      <thead>
        <tr>
          <th>Equity versus your opponents continuing range</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td v-if="equitySimulationsInProgress">Running simulations...</td>
          <td v-else>{{ equity }}%</td>
        </tr>
      </tbody>
    </table>
    <div class="form-group">
      <label>
        True breakeven percentage:
        <input
          type="number"
          class="form-control input-lg"
          v-bind:value="profitableBreakevenPercentage"
          v-on:input="$emit('setProfitableBreakevenPercentage', $event.target.valueAsNumber)"
          min="1"
          v-bind:max="immediatelyProfitableBreakevenPercentage"
        >
      </label>
    </div>
    <div v-if="isBluffProfitable" class="alert alert-success">
      According to the true breakeven percentage that you estimated, your bluff <em>will</em> be profitable. ✅
    </div>
    <div v-else class="alert alert-danger">
      According to the true breakeven percentage that you estimated, your bluff <em>will not</em> be profitable. ❌
    </div>
    <hr>
    <p>
      1. Suppose that you bluff with a flush draw, get called, and then hit your flush. "Immediately profitable" doesn't take this into account.
      <toggle-help-button
        v-bind:helpAreaVisible="helpAreaVisible.futureStreets[0]"
        v-on:toggleHelp="toggleHelp('futureStreets', 0)"
      />
    </p>
    <future-streets-help-section-1 v-if="helpAreaVisible.futureStreets[0]"/>
    <p>
      2. Suppose that you bluff, get called, hit a scare card, bluff again on the next street, and get a fold. "Immediately profitable" doesn't take this into account either.
      <toggle-help-button
        v-bind:helpAreaVisible="helpAreaVisible.futureStreets[1]"
        v-on:toggleHelp="toggleHelp('futureStreets', 1)"
      />
    </p>
    <future-streets-help-section-2 v-if="helpAreaVisible.futureStreets[1]"/>
    <p>So then, use your judgement to <strong>drop the {{ immediatelyProfitableBreakevenPercentage }}% number down by about 5-15%</strong> depending on whether other factors are good or bad, and then enter that number in to the input box above.</p>
    <table class="table">
      <thead>
        <tr>
          <th>Good factors</th>
          <th>Bad factors</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>A lot of outs</td>
          <td>Not many outs</td>
        </tr>
        <tr>
          <td>Good future fold equity</td>
          <td>Bad future fold equity</td>
        </tr>
        <tr>
          <td>No showdown value</td>
          <td>Showdown value</td>
        </tr>
        <tr>
          <td>In position</td>
          <td>Out of position</td>
        </tr>
      </tbody>
    </table>
    <p>Or, play around with the <router-link to="/breakeven-percentage-for-bluffing-the-turn">breakeven percentage tool</router-link> to come up with a more exact number.</p>
    <p v-if="streetToBluff === 'flop'">(Right now it only works for turn bluffs, not flop bluffs. In the future it will work for flop bluffs as well. For now, if you're considering a flop bluff, drop whatever number you come up with for a turn bluff down a little bit.)</p>
  </section>
</template>

<script>
import ToggleHelpButton from '@/components/guided-analyses/help/toggle-help-button';
import FutureStreetsHelpSectionOne from '@/components/guided-analyses/should-you-bluff/help/future-streets-1';
import FutureStreetsHelpSectionTwo from '@/components/guided-analyses/should-you-bluff/help/future-streets-2';

export default {
  name: 'should-you-bluff-final-conclusion',
  components: {
    'toggle-help-button': ToggleHelpButton,
    'future-streets-help-section-1': FutureStreetsHelpSectionOne,
    'future-streets-help-section-2': FutureStreetsHelpSectionTwo,
  },
  props: [
    'equitySimulationsInProgress',
    'ableToRunEquityCalculations',
    'equity',
    'bluffSize',
    'streetToBluff',
    'hitPercentageForStreetToBluff',
    'foldEquity',
    'immediatelyProfitableBreakevenPercentage',
    'profitableBreakevenPercentage',
  ],
  data: function () {
    return {
      helpAreaVisible: {
        futureStreets: [false, false, false],
      },
    };
  },
  computed: {
    isBluffProfitable: function () {
      return this.foldEquity > this.profitableBreakevenPercentage;
    },
  },
  watch: {
    bluffSize: function (newBluffSize, oldBluffSize) {
      let newProfitableBreakevenPercentage = Math.round(this.immediatelyProfitableBreakevenPercentage - 5);

      this.$emit('setProfitableBreakevenPercentage', newProfitableBreakevenPercentage);
    },
  },
  methods: {
    toggleHelp: function (type, index) {
      if (type === 'futureStreets') {
        this.helpAreaVisible.futureStreets[index] = !this.helpAreaVisible.futureStreets[index];
      }

      this.$forceUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import 'src/assets/help-section';

  .should-you-bluff-final-conclusion {
    font-size: 18px;

    pre {
      font-size: 16px;
    }

    input[type=number].form-control {
      font-size: 18px;
    }

    h1, h3, li, p, table {
      max-width: 800px;
    }

    .well {
      margin-top: 10px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/guided-analyses/should-you-bluff/conclusions/final-conclusion.vue